import React, { useState, useEffect, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import './PeriodicReports.css';
import ReactDOMServer from 'react-dom/server';
import Button from 'react-bootstrap/Button';
import traIcon from '../media/icons/logo.png';
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormControl from "@material-ui/core/FormControl";
import { Autocomplete } from '@material-ui/lab';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { hasGlobalStations, scopeStations, allowedSerials } from '../components/roleAccess';
import { API_BASE } from '../config';
import { tr } from '../i18n';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#bdc3c7',
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

// --- Shift-based date range calculation ---------------------------------
// The current_day/previous_day/current_month/previous_month endpoints only
// receive shift_period + station_serial — they never return the actual
// computed date range in their response. These helpers reconstruct that
// range on the frontend so the printed report can show a START/END, using
// the standard shift-day convention: a "day" runs from shift_period today
// to shift_period the next calendar day, not midnight-to-midnight.
//
// NOTE: this is a display-only approximation of what the backend computes
// internally. If it ever looks off by a day/hour compared to the real data
// shown in the table, the backend's own shift-boundary logic differs from
// this and should be treated as the source of truth instead.
function pad2(n) { return String(n).padStart(2, '0'); }

function formatDT(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

function parseShift(shiftPeriod) {
  const [h, m] = (shiftPeriod || '07:00').split(':').map(Number);
  return { h: h || 0, m: m || 0 };
}

function currentDayRange(shiftPeriod) {
  const { h, m } = parseShift(shiftPeriod);
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0);
  if (now < start) start.setDate(start.getDate() - 1);
  return { start, end: now };
}

function previousDayRange(shiftPeriod) {
  const { start: curStart } = currentDayRange(shiftPeriod);
  const start = new Date(curStart);
  start.setDate(start.getDate() - 1);
  return { start, end: curStart };
}

function currentMonthRange(shiftPeriod) {
  const { h, m } = parseShift(shiftPeriod);
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1, h, m, 0);
  return { start, end: now };
}

function previousMonthRange(shiftPeriod) {
  const { h, m } = parseShift(shiftPeriod);
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1, h, m, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), 1, h, m, 0);
  return { start, end };
}

const ReceiptContent = ({data, dataArray, station, stationInfo, fallbackStart, fallbackEnd}) => {
  return (
      <div style={{ margin: '100px 40px 100px 40px', border: '1px solid black' }}>
          <h3 style={{ textAlign: 'center', fontWeight: 'bold' }}>{tr("****START OF PERIODIC REPORT****")}</h3>
          <img src={traIcon} alt={tr("logo")} style={{ display: 'block', margin: '0 auto'}} width="50em" height="50em"/>

          <h4 style={{ textAlign: 'center', margin: '0' , fontWeight: 'bold' }}> {station}</h4>
          <p style={{ textAlign: 'center', margin: '0' }}><b>{tr("MOBILE:")} </b>{stationInfo?.ContactPersonPhone ?? ''}</p>
          <p style={{ textAlign: 'center', margin: '0' }}><b>{tr("TIN:")} </b>{stationInfo?.OperatorTin ?? ''}</p>
          <p style={{ textAlign: 'center', margin: '0' }}><b>{tr("VRN:")} </b>{stationInfo?.OperatorVrn ?? ''}</p>
          <p style={{ textAlign: 'center', margin: '0' }}><b>{tr("SERIAL NUMBER:")} </b>{stationInfo?.LicenseeTraSerialNo ?? ''}</p>
          <p style={{ textAlign: 'center', margin: '0' }}><b>{tr("UIN:")} </b>{stationInfo?.OperatorUIN ?? ''}</p>
          <p style={{ textAlign: 'center', margin: '0' }}><b>{tr("TAX OFFICE:")} {stationInfo?.Tax_office ?? ''}</b></p>

          <hr style={{ border: 'dashed 1px' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span><b>{tr("START:")} </b>{
                data?.dates?.start ?? data?.data?.dates?.start ??
                data?.start_date ?? data?.data?.start_date ??
                data?.period?.start ?? data?.data?.period?.start ??
                data?.since_date ?? data?.data?.since_date ??
                fallbackStart ?? ''
              }</span>
              <span><b>{tr("END:")} </b>{
                data?.dates?.end ?? data?.data?.dates?.end ??
                data?.end_date ?? data?.data?.end_date ??
                data?.period?.end ?? data?.data?.period?.end ??
                data?.to_date ?? data?.data?.to_date ??
                fallbackEnd ?? ''
              }</span>
          </div>
          <hr style={{ border: 'dashed 1px' }} />

          <p style={{ textAlign: 'left' }}><b>{tr("TOTAL LITRE:")} </b>{tr("{0} Ltr", parseFloat(data?.totals?.total_volume || data?.data?.totals?.total_volume || 0).toLocaleString('en-US'))}</p>
          <p style={{ textAlign: 'left' }}><b>{tr("TOTAL AMOUNT:")} </b>{parseFloat(data?.totals?.total_amount || data?.data?.totals?.total_amount || 0).toLocaleString('en-US')}</p>
          <p style={{ textAlign: 'left' }}><b>{tr("DISCOUNT AMOUNT:")} </b>0</p>
          <p style={{ textAlign: 'left' }}><b>{tr("NET AMOUNT:")} </b>{parseFloat(data?.totals?.net_amount || data?.data?.totals?.net_amount || 0).toLocaleString('en-US')}</p>

          <hr style={{ border: 'dashed 1px' }} />
          <h3 style={{ textAlign: 'center' }}>{tr("Product-Wise Details")}</h3>
          
          <div style={{ margin: '0 20px' }}>
          <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
              <thead> 
                  <tr style={{ backgroundColor: '#bdc3c7' }}>
                      <td style={{ border: '1px solid black', padding: '8px' }}>{tr("Product")}</td>
                      <td style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>{tr("Amount")}</td>
                      <td style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>{tr("Volume (L)")}</td>
                      <td style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>{tr("Receipts")}</td>
                  </tr>
              </thead>
              <tbody>
                  {(dataArray || []).map((row, index) => (
                      <tr key={index}>
                          <td style={{ border: '1px solid black', padding: '8px' }}>{row.product || row.product_name || row.item}</td>
                          <td style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>{parseFloat(row.total_amount || row.amount || 0).toLocaleString('en-US')}</td>
                          <td style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>{parseFloat(row.total_volume || row.volume || 0).toLocaleString('en-US')}</td>
                          <td style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>{row.total_receipts || row.receipts || 0}</td>
                      </tr>
                  ))}
              </tbody>
          </table>
    </div>      
          <h3 style={{ textAlign: 'center', marginTop: '20px' }}>{tr("****END OF PERIODIC REPORT****")}</h3>
      </div>
  );
};

const openReceiptPopup = (data, dataArray, station, stationInfo, fallbackStart, fallbackEnd) => {
  const receiptContent = ReactDOMServer.renderToString(
    <ReceiptContent data={data} dataArray={dataArray} station={station} stationInfo={stationInfo} fallbackStart={fallbackStart} fallbackEnd={fallbackEnd} />
  );
  const receiptWindow = window.open('', '_blank', 'width=600,height=800,toolbar=no,menubar=no');

  receiptWindow.document.write('<html><head><title>Receipt</title></head><body>');
  receiptWindow.document.write('<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.1/css/all.min.css">');
  receiptWindow.document.write(`
    <style>
        .toolbar {
            display: flex; 
            justify-content: space-between; 
            padding: 10px; 
            background-color: #f5f5f5; 
            border-bottom: 1px solid #e0e0e0;
        }
        .toolbar-button {
            background: none; 
            border: none; 
            cursor: pointer;
        }
        @media print {
            .toolbar {
                display: none;
            }
        }
    </style>
    `);

  receiptWindow.document.write(`
    <div class="toolbar">
        <span>Periodic Record</span>
        <div>
            <button onclick="window.print()" class="toolbar-button"><i class="fas fa-print"></i></button>
            <button id="download" class="toolbar-button"><i class="fas fa-download"></i></button>
        </div>
    </div>
    `);

  receiptWindow.document.write(receiptContent);
  receiptWindow.document.write('</body></html>');
  receiptWindow.document.close();

  receiptWindow.onload = function () {
    const downloadButton = receiptWindow.document.getElementById('download');
    downloadButton.addEventListener('click', function () {
      html2canvas(receiptWindow.document.body, { dpi: 600 }).then((canvas) => {
        const imgData = canvas.toDataURL('image/jpeg', 2.0);
        const pdf = new jsPDF('p', 'mm', 'a4');
        pdf.addImage(imgData, 'PNG', 0, 0, 210, 320);

        // Build a descriptive filename from the same resolved dates shown
        // on the receipt itself (API value if present, otherwise the
        // computed fallback), e.g. receipt_2026-07-01_to_2026-07-12.pdf
        const resolvedStart =
          data?.dates?.start ?? data?.data?.dates?.start ??
          data?.start_date ?? data?.data?.start_date ??
          data?.period?.start ?? data?.data?.period?.start ??
          data?.since_date ?? data?.data?.since_date ??
          fallbackStart ?? '';
        const resolvedEnd =
          data?.dates?.end ?? data?.data?.dates?.end ??
          data?.end_date ?? data?.data?.end_date ??
          data?.period?.end ?? data?.data?.period?.end ??
          data?.to_date ?? data?.data?.to_date ??
          fallbackEnd ?? '';

        const toDatePart = (s) => String(s).slice(0, 10).replace(/[^0-9-]/g, ''); // "YYYY-MM-DD"
        const startPart = resolvedStart ? toDatePart(resolvedStart) : 'unknown-start';
        const endPart = resolvedEnd ? toDatePart(resolvedEnd) : 'unknown-end';

        pdf.save(`receipt_${startPart}_to_${endPart}.pdf`);
      });
    });
  };
};

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minWidth: 120,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  FilterClearButtonClass: {
    float: 'right',
    margin: 10,
  },
}));

export default function CustomizedTables({token, selectedStationFromSearch}) {
  const [currentDay, setCurrentDay] = useState({});
  const [previousDay, setPreviousDay] = useState({});
  const [currentMonth, setCurrentMonth] = useState({});
  const [previousMonth, setPreviousMonth] = useState({});
  const [customPeriod, setCustomPeriod] = useState({});
  const [currentDayArray, setCurrentDayArray] = useState([]);
  const [previousDayArray, setPreviousDayArray] = useState([]);
  const [currentMonthArray, setCurrentMonthArray] = useState([]);
  const [previousMonthArray, setPreviousMonthArray] = useState([]);
  const [customPeriodArray, setCustomPeriodArray] = useState([]);
  const [state, setState] = useState({ shift_period: "07:00", station_serial: ""});
  const [customState, setCustomState] = useState({since_date: "", to_date: ""});
  const [stationsArray, setStationsArray] = useState([]);
  const [selectedStation, setSelectedStation] = useState("");
  // Full station records (TIN, VRN, serial, UIN, mobile, tax office, etc.)
  // fetched from /stationinfo/all - the same endpoint Settings.js uses to
  // get complete station details. /station/all (used for stationsArray
  // above) only returns enough for the station-name dropdown and is
  // filtered by is_vfd, so it can't be relied on for these fields.
  const [stationInfoArray, setStationInfoArray] = useState([]);
  // Full station record for whichever station is currently selected -
  // used to fill in the printed receipt's identity fields instead of a
  // fixed test station.
  const [selectedStationInfo, setSelectedStationInfo] = useState(null);
  const classes = useStyles();

  const extractResponseArray = (responseObj) => {
    if (!responseObj) return [];
    if (Array.isArray(responseObj)) return responseObj;
    if (Array.isArray(responseObj.data)) return responseObj.data;
    
    const nestedData = responseObj.data || responseObj;
    if (nestedData && typeof nestedData === 'object') {
      if (Array.isArray(nestedData.page_records)) return nestedData.page_records;
      if (Array.isArray(nestedData.records)) return nestedData.records;
      if (Array.isArray(nestedData.periodic_data)) return nestedData.periodic_data;
      if (Array.isArray(nestedData.products)) return nestedData.products;
      
      const foundKey = Object.keys(nestedData).find(key => Array.isArray(nestedData[key]));
      if (foundKey) return nestedData[foundKey];
    }
    return [];
  };

  const handleChange = (e) => {
    const name = e.target.name;
    let value = e.target.value;
  
    if (name !== 'station_serial' && name !== 'since_date' && name !== 'to_date') {
      if (!isNaN(value) && value.length > 0) {
        value = Number(value);
      }
    }
  
    if ((name === 'since_date' || name === 'to_date') && typeof value === 'string') {
      value = value.replace('T', ' ');
      if (value.length === 16) {
        value += ':00';
      }
      setCustomState((prevState) => ({ ...prevState, [name]: value }));
    } else {
      setState((prevState) => ({ ...prevState, [name]: value }));
    }
  };

  const findStationInfo = useCallback((serial) => {
    if (!serial) return null;
    return stationInfoArray.find(
      (item) => String(item.LicenseeTraSerialNo) === String(serial) || String(item.serial_number) === String(serial)
    ) || null;
  }, [stationInfoArray]);

  const handleStationDropdownChange = (e) => {
    const serial = String(e.target.value);
    setState((prevState) => ({ ...prevState, station_serial: serial }));
    
    const found = stationsArray.find(
      (item) => String(item.serial_number) === serial || String(item.LicenseeTraSerialNo) === serial
    );
    if (found) {
      setSelectedStation(found.RetailStationName || found.name || "");
    }
    setSelectedStationInfo(findStationInfo(serial) || found || null);
  };

  useEffect(() => {
    if (selectedStationFromSearch) {
      const serial = String(selectedStationFromSearch.LicenseeTraSerialNo || selectedStationFromSearch.serial_number || "");
      if (serial) {
        setState((prevState) => ({ ...prevState, station_serial: serial }));
        setSelectedStation(selectedStationFromSearch.RetailStationName || selectedStationFromSearch.name || "");
        setSelectedStationInfo(findStationInfo(serial) || selectedStationFromSearch);
      }
    }
  }, [selectedStationFromSearch, stationInfoArray, findStationInfo]);

  useEffect(() => {
    try {
      const storedToken = sessionStorage.getItem('token');
      const accessToken = storedToken ? JSON.parse(storedToken)?.data?.access_token : '';
      if (!accessToken) return;

      fetch(`${API_BASE}/station/all`, {
        headers: { Authorization: accessToken },
      })
        .then((response) => response.json())
        .then((data) => setStationsArray(scopeStations(token, (data.data || []).filter(item => item.is_vfd))));
    } catch(e) { console.error(e); }
  }, [token]);

  // Full station details (TIN, VRN, serial, UIN, mobile, tax office) come
  // from /stationinfo/all - the same endpoint Settings.js uses. /station/all
  // above doesn't carry these fields.
  useEffect(() => {
    try {
      const storedToken = sessionStorage.getItem('token');
      const accessToken = storedToken ? JSON.parse(storedToken)?.data?.access_token : '';
      if (!accessToken) return;

      fetch(`${API_BASE}/stationinfo/all`, {
        headers: { Authorization: accessToken },
      })
        .then((response) => response.json())
        .then((data) => setStationInfoArray(data.data || []));
    } catch(e) { console.error(e); }
  }, [token]);

  useEffect(() => {
    if (hasGlobalStations(token)) return;
    const mine = allowedSerials(token) || [];
    if (mine.length === 1) {
      const serial = String(mine[0]);
      setState((prev) => ({ ...prev, station_serial: serial }));
      const found = stationsArray.find((s) => String(s.serial_number) === serial || String(s.LicenseeTraSerialNo) === serial);
      if (found) {
        setSelectedStation(found.RetailStationName || found.name || "");
      }
      setSelectedStationInfo(findStationInfo(serial) || found || null);
    }
  }, [token, stationsArray, stationInfoArray, findStationInfo]);

  useEffect(() => {
    if (!state.station_serial) return;

    try {
      const storedToken = sessionStorage.getItem('token');
      const accessToken = storedToken ? JSON.parse(storedToken)?.data?.access_token : '';
      if (!accessToken) return;

      let values = Object.fromEntries(Object.entries(state).filter(([_, v]) => v !== ''));  

      const fetchData = (url, setData, setArrayData) => {
          fetch(url, {
              method: 'POST',
              headers: {
                  'Authorization': accessToken,
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify(values)
          })
          .then(response => response.json())
          .then(data => {
              setData(data);
              setArrayData(extractResponseArray(data));
          });
      };

      fetchData(`${API_BASE}/sales_reports/periodic/current_day`, setCurrentDay, setCurrentDayArray);
      fetchData(`${API_BASE}/sales_reports/periodic/previous_day`, setPreviousDay, setPreviousDayArray);
      fetchData(`${API_BASE}/sales_reports/periodic/current_month`, setCurrentMonth, setCurrentMonthArray);
      fetchData(`${API_BASE}/sales_reports/periodic/previous_month`, setPreviousMonth, setPreviousMonthArray);
    } catch(e) { console.error(e); }
  }, [state]);

  useEffect(() => {
    if (customState.since_date === "" || customState.to_date === "" || state.station_serial === "") return;

    try {
      const storedToken = sessionStorage.getItem('token');
      const accessToken = storedToken ? JSON.parse(storedToken)?.data?.access_token : '';
      if (!accessToken) return;

      const payload = {
        since_date: customState.since_date,
        to_date: customState.to_date,
        station_serial: state.station_serial
      };

      const fetchData = (url, setData, setArrayData) => {
        fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': accessToken,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        })
        .then(response => response.json())
        .then(data => {
          setData(data);
          setArrayData(extractResponseArray(data));
        });
      };

      fetchData(`${API_BASE}/sales_reports/periodic`, setCustomPeriod, setCustomPeriodArray);
    } catch(e) { console.error(e); }
  }, [customState.since_date, customState.to_date, state.station_serial]);

  return (
    <div>
      <div className="FilterBar">
      <p><b>{tr("Select shift period (Start and end of shift).")}</b></p>
        <FormControl className={classes.formControl} noValidate>
          <TextField
              name="shift_period"
              onChange={handleChange}
              value={state.shift_period}
              id="date"
              label={tr("Shift")}
              type="time"
              className={classes.textField}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: "60" }}
          />
        <FormHelperText className="FormHelperText">
          {tr("Select Shift Period (Start and end times will be the same)")}
        </FormHelperText>
      </FormControl>

      <div>
  {!(hasGlobalStations(token) || stationsArray.length > 1) ? (
    <div>
    <FormControl className={classes.formControl} noValidate>
      <TextField
        name="since_date"
        onChange={handleChange}
        value={customState.since_date}
        id="date"
        label={tr("Custom Starting Date")}
        type="datetime-local"
        className={classes.textField}
        InputLabelProps={{ shrink: true }}
        inputProps={{ step: "1" }}
      />
      <FormHelperText className="FormHelperText">{tr("Since")}</FormHelperText>
    </FormControl>

    <FormControl className={classes.formControl} noValidate>
      <TextField
        name="to_date"
        onChange={handleChange}
        value={customState.to_date}
        id="date"
        label={tr("Custom End Date")}
        type="datetime-local"
        className={classes.textField}
        InputLabelProps={{ shrink: true }}
        inputProps={{ step: "1" }}
      />
      <FormHelperText className="FormHelperText">{tr("To")}</FormHelperText>
    </FormControl>
    </div>
  ) : (
    <div>
    <FormControl style={{ minWidth: 250 }}>
      <Autocomplete
        options={stationsArray}
        getOptionLabel={(option) => option.RetailStationName || option.name || ''}
        value={
          stationsArray.find(
            (item) => String(item.serial_number || item.LicenseeTraSerialNo) === state.station_serial
          ) || null
        }
        onChange={(event, newValue) => {
          const serial = newValue ? String(newValue.serial_number || newValue.LicenseeTraSerialNo || '') : '';
          handleStationDropdownChange({ target: { value: serial } });
        }}
        renderInput={(params) => (
          <TextField {...params} placeholder={tr("Search stations...")} />
        )}
      />
      <FormHelperText>{tr("Available Stations")}</FormHelperText>
    </FormControl>
          <br/>
          <br/>
    <FormControl className={classes.formControl} noValidate>
      <TextField
        name="since_date"
        onChange={handleChange}
        value={customState.since_date}
        id="date"
        label={tr("Custom Starting Date")}
        type="datetime-local"
        className={classes.textField}
        InputLabelProps={{ shrink: true }}
        inputProps={{ step: "1" }}
      />
      <FormHelperText className="FormHelperText">{tr("Since")}</FormHelperText>
    </FormControl>

    <FormControl className={classes.formControl} noValidate>
      <TextField
        name="to_date"
        onChange={handleChange}
        value={customState.to_date}
        id="date"
        label={tr("Custom End Date")}
        type="datetime-local"
        className={classes.textField}
        InputLabelProps={{ shrink: true }}
        inputProps={{ step: "1" }}
      />
      <FormHelperText className="FormHelperText">{tr("To")}</FormHelperText>
    </FormControl>
    </div>
  )}
</div>
        </div>

    <div className="PeriodicContainer">
        <div className="PeriodicComponent">
          <TableContainer component={Paper}>
      <Table sx={{ minWidth: 700 }} aria-label={tr("customized table")}>
        <TableHead>
          <TableRow>
            <StyledTableCell>{tr("Daily (Current)")}</StyledTableCell>
            <StyledTableCell align="right">{tr("Amount (TL)")}</StyledTableCell>
            <StyledTableCell align="right">{tr("Volume (L)")}</StyledTableCell>
            <StyledTableCell align="right">{tr("Receipts")}</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(currentDayArray || []).map((row, index) => (
            <StyledTableRow key={index}>
              <StyledTableCell component="th" scope="row">
                {row.product || row.product_name || row.item}
              </StyledTableCell>
              <StyledTableCell align="right">{parseFloat(row.total_amount || row.amount || 0).toLocaleString('en-US')}</StyledTableCell>
              <StyledTableCell align="right">{parseFloat(row.total_volume || row.volume || 0).toLocaleString('en-US')}</StyledTableCell>
              <StyledTableCell align="right">{row.total_receipts || row.receipts || 0}</StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    <Button variant="primary" onClick={() => {
      const { start, end } = currentDayRange(state.shift_period);
      openReceiptPopup(currentDay, currentDayArray, selectedStation, selectedStationInfo, formatDT(start), formatDT(end));
    }}>{tr("Print")}</Button>
    </div>
        <div className="PeriodicComponent">
        <TableContainer component={Paper}>
        <Table sx={{ minWidth: 700 }} aria-label={tr("customized table")}>
        <TableHead>
          <TableRow>
            <StyledTableCell>{tr("Daily (Previous)")}</StyledTableCell>
            <StyledTableCell align="right">{tr("Amount")}</StyledTableCell>
            <StyledTableCell align="right">{tr("Volume (L)")}</StyledTableCell>
            <StyledTableCell align="right">{tr("Receipts")}</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(previousDayArray || []).map((row, index) => (
            <StyledTableRow key={index}>
              <StyledTableCell component="th" scope="row">
                {row.product || row.product_name || row.item}
              </StyledTableCell>
              <StyledTableCell align="right">{parseFloat(row.total_amount || row.amount || 0).toLocaleString('en-US')}</StyledTableCell>
              <StyledTableCell align="right">{parseFloat(row.total_volume || row.volume || 0).toLocaleString('en-US')}</StyledTableCell>
              <StyledTableCell align="right">{row.total_receipts || row.receipts || 0}</StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    <Button variant="primary" onClick={() => {
      const { start, end } = previousDayRange(state.shift_period);
      openReceiptPopup(previousDay, previousDayArray, selectedStation, selectedStationInfo, formatDT(start), formatDT(end));
    }}>{tr("Print")}</Button>
        </div>
        <div className="PeriodicComponent"><TableContainer component={Paper}>
        <Table sx={{ minWidth: 700 }} aria-label={tr("customized table")}>
        <TableHead>
          <TableRow>
            <StyledTableCell>{tr("Monthly (Current)")}</StyledTableCell>
            <StyledTableCell align="right">{tr("Amount (TL)")}</StyledTableCell>
            <StyledTableCell align="right">{tr("Volume (L)")}</StyledTableCell>
            <StyledTableCell align="right">{tr("Receipts")}</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(currentMonthArray || []).map((row, index) => (
            <StyledTableRow key={index}>
              <StyledTableCell component="th" scope="row">
                {row.product || row.product_name || row.item}
              </StyledTableCell>
              <StyledTableCell align="right">{parseFloat(row.total_amount || row.amount || 0).toLocaleString('en-US')}</StyledTableCell>
              <StyledTableCell align="right">{parseFloat(row.total_volume || row.volume || 0).toLocaleString('en-US')}</StyledTableCell>
              <StyledTableCell align="right">{row.total_receipts || row.receipts || 0}</StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    <Button variant="primary" onClick={() => {
      const { start, end } = currentMonthRange(state.shift_period);
      openReceiptPopup(currentMonth, currentMonthArray, selectedStation, selectedStationInfo, formatDT(start), formatDT(end));
    }}>{tr("Print")}</Button>
    </div>
        <div className="PeriodicComponent"><TableContainer component={Paper}>
        <Table sx={{ minWidth: 700 }} aria-label={tr("customized table")}>
        <TableHead>
          <TableRow>
            <StyledTableCell>{tr("Monthly (Previous)")}</StyledTableCell>
            <StyledTableCell align="right">{tr("Amount (TL)")}</StyledTableCell>
            <StyledTableCell align="right">{tr("Volume (L)")}</StyledTableCell>
            <StyledTableCell align="right">{tr("Receipts")}</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(previousMonthArray || []).map((row, index) => (
            <StyledTableRow key={index}>
              <StyledTableCell component="th" scope="row">
                {row.product || row.product_name || row.item}
              </StyledTableCell>
              <StyledTableCell align="right">{parseFloat(row.total_amount || row.amount || 0).toLocaleString('en-US')}</StyledTableCell>
              <StyledTableCell align="right">{parseFloat(row.total_volume || row.volume || 0).toLocaleString('en-US')}</StyledTableCell>
              <StyledTableCell align="right">{row.total_receipts || row.receipts || 0}</StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    <Button variant="primary" onClick={() => {
      const { start, end } = previousMonthRange(state.shift_period);
      openReceiptPopup(previousMonth, previousMonthArray, selectedStation, selectedStationInfo, formatDT(start), formatDT(end));
    }}>{tr("Print")}</Button>
    </div>
    </div>
    <div className="PeriodicContainer2">
    <div className="PeriodicComponent"><TableContainer component={Paper}>
        <Table sx={{ minWidth: 700 }} aria-label={tr("customized table")}>
        <TableHead>
          <TableRow>
            <StyledTableCell>{tr("Custom")}</StyledTableCell>
            <StyledTableCell align="right">{tr("Amount (TL)")}</StyledTableCell>
            <StyledTableCell align="right">{tr("Volume (L)")}</StyledTableCell>
            <StyledTableCell align="right">{tr("Receipts")}</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(customPeriodArray || []).map((row, index) => (
            <StyledTableRow key={index}>
              <StyledTableCell component="th" scope="row">
                {row.product || row.product_name || row.item}
              </StyledTableCell>
              <StyledTableCell align="right">{parseFloat(row.total_amount || row.amount || 0).toLocaleString('en-US')}</StyledTableCell>
              <StyledTableCell align="right">{parseFloat(row.total_volume || row.volume || 0).toLocaleString('en-US')}</StyledTableCell>
              <StyledTableCell align="right">{row.total_receipts || row.receipts || 0}</StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    <Button variant="primary" onClick={() => openReceiptPopup(customPeriod, customPeriodArray, selectedStation, selectedStationInfo, customState.since_date, customState.to_date)}>{tr("Print")}</Button>
    </div>
    </div>
    </div>
  );
}